/**
 * WhyTrace — the shared "Why am I seeing this?" disclosure for Play cards
 * (Normal Core §6.5.8 / §12-8). A quiet toggle that, when expanded, lazily
 * fetches the card's source-event causal ancestry (`/api/events/chain` through
 * the rail — never a bare fetch) and paints it root-cause-first as a
 * CausalTraceView. Lazy by construction: the SWR hook only runs while the chain
 * is shown, so a card the user never asks "why" about costs nothing.
 *
 * One implementation, two Play surfaces: the fullscreen takeover card
 * (`play-takeover.tsx`, dark room) and the inline home suggestions list
 * (`play-list.tsx`, light card). The surface-specific chrome (the expanded
 * panel's border/background) is passed via `panelClassName`; the toggle and the
 * fetch/render core are identical. Only event-derived cards (commitment-risk
 * intel) carry a `causationUid`, so callers gate on it — the affordance never
 * renders for a card with no trace.
 */

import { useState } from "react";
import { useApi } from "../../api/client";
import { eventsChain } from "../../api/contracts/events";
import { CausalTraceView } from "../causal-trace-view";

export interface WhyTraceProps {
  /** The source event uid (PlayAction.causationUid) whose causal ancestry to walk. */
  uid: string;
  /** The source aggregate kind, folded into the chain query so the trace includes
   *  that aggregate's lifecycle history for depth (a ≥3-link trace). */
  kind?: string;
  /** Classes for the expanded trace container — surface-specific (the dark-room
   *  takeover passes a translucent-white panel; the light home list omits it and
   *  takes the muted default). */
  panelClassName?: string;
}

export function WhyTrace({ uid, kind, panelClassName }: WhyTraceProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        // stopPropagation so the toggle never triggers an enclosing clickable row
        // (the home suggestion row opens the Play driver on click).
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="self-start type-body font-medium text-muted-foreground/70 underline-offset-4 transition-colors hover:text-foreground hover:underline"
      >
        {open ? "Hide reasoning" : "Why am I seeing this?"}
      </button>
      {open ? <WhyTraceChain uid={uid} kind={kind} panelClassName={panelClassName} /> : null}
    </div>
  );
}

function WhyTraceChain({
  uid,
  kind,
  panelClassName,
}: {
  uid: string;
  kind?: string;
  panelClassName?: string;
}) {
  const { data, error, isLoading } = useApi(eventsChain, {
    query: kind ? { uid, kind } : { uid },
  });
  if (isLoading) {
    return <p className="type-subtext text-muted-foreground/60">Tracing the cause…</p>;
  }
  if (error || !data || data.chain.length === 0) {
    return (
      <p className="type-subtext text-muted-foreground/60">
        No causal trace recorded for this recommendation yet.
      </p>
    );
  }
  return (
    <CausalTraceView
      chain={data.chain}
      wrapInCard={false}
      className={
        panelClassName ??
        "rounded-xl border border-border/60 bg-muted/30 px-2 py-2 duration-150 animate-in fade-in-0"
      }
    />
  );
}
